import TransactionsIn from '../transaction-summary-tables/transactions-in/TransactionsIn';
import TransactionsOut from '../transaction-summary-tables/transactions-out/TransactionsOut';
import InvestmentPriceMovement from '../transaction-summary-tables/investment-price-movement/InvestmentPriceMovement';
import InvestmentBalances from '../transaction-summary-tables/investment-balances/InvestmentBalances';
import TransactionSummaryExplanation from '../transaction-summary-tables/transaction-summary-explanation/TransactionSummaryExplanation';
import InternalRateOfReturnTable from '../transaction-summary-tables/internal-rate-of-return/InternalRateOfReturnTable';
import { useFormatCurrency } from 'modules/dashboard/utils/formatting/useFormatCurrency';
import { TransactionSummaryDropdownProps } from 'modules/clients/types/transaction-summary';
import { Typography } from '@sanlam-glacier/app-common/components';
import styles from './transaction-summary-dropdown.module.scss';
import StatementDropdown from 'modules/clients/components/atoms/statement-dropdown/StatementDropdown';
import TransactionSummaryHeadingRow from 'modules/clients/components/atoms/transaction-summary-rows/TransactionSummaryHeadingRow';
import { Show } from '@sanlam-glacier/app-common/components';

const TransactionSummaryDropdown = ({
  investmentCount,
  investmentTotal,
  transactionSummary,
  index,
  includeIRR
}: TransactionSummaryDropdownProps) => {
  const formatDate = (date?: Date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { formatCurrency } = useFormatCurrency();

  const subtitle = () => {
    return (
      <div className={styles.subtitleContainer}>
        <Typography variant={Typography.VARIANT.B2} weight={Typography.WEIGHT.MEDIUM}>
          Transaction summary:
        </Typography>
        &nbsp;
        <Typography variant={Typography.VARIANT.B2} weight={Typography.WEIGHT.NORMAL}>
          {`${formatDate(transactionSummary.transactionSummary.openingBalanceDate)} to ${formatDate(transactionSummary.transactionSummary.closingBalanceDate)}`}
        </Typography>
      </div>
    );
  };

  return (
    <>
      <StatementDropdown
        data-testid='transactionSummaryDropdownContainer'
        title={`${transactionSummary.transactionSummary.planNumber}: ${transactionSummary.transactionSummary.planName}`}
        subtitle={subtitle()}
        titleDescription={`Investment inception date: ${formatDate(transactionSummary?.transactionSummary.inceptionDate)}`}
        statementDescription={`${investmentCount} of ${investmentTotal} investments`}
        shoudShowCollapse={investmentTotal !== 1}
        buttonTitle='summary'
        // specifiedDate={`YYYY-MM-DD`}
        testid={`transactionSummary${index}`}
      >
        {({ isDropdownCollapsed }) => (
          <div>
            {isDropdownCollapsed ? (
              <>
                <InvestmentBalances
                  data-testid='transactionSummaryInvestmentBalancesTable'
                  openingBalance={`${formatCurrency(transactionSummary.transactionSummary.openingBalance)}`}
                  openingBalanceDate={`${formatDate(transactionSummary.transactionSummary.openingBalanceDate)}`}
                  closingBalance={`${formatCurrency(transactionSummary.transactionSummary.closingBalance)}`}
                  closingBalanceDate={`${formatDate(transactionSummary.transactionSummary.closingBalanceDate)}`}
                />
                <TransactionsIn
                  data-testid='transactionSummaryTransactionsInTable'
                  collectiveInterest={`${formatCurrency(transactionSummary.transactionSummary.collectiveInterest)}`}
                  moneyMarketInterest={`${formatCurrency(transactionSummary.transactionSummary.marketFundsInterest)}`}
                  dividends={`${formatCurrency(transactionSummary.transactionSummary.dividendAmount)}`}
                  otherIncome={`${formatCurrency(transactionSummary.transactionSummary.otherIncomeAmount)}`}
                  income={`${formatCurrency(transactionSummary.transactionSummary.netIncome)}`}
                />
                <TransactionsOut
                  data-testid='transactionSummaryTransactionsOutTable'
                  withdrawal={`${formatCurrency(transactionSummary.transactionSummary.withdrawal)}`}
                  adminFee={`${formatCurrency(transactionSummary.transactionSummary.annualAdministrationFees)}`}
                  adminFeeVat={`${formatCurrency(transactionSummary.transactionSummary.vatOnAnnualAdministrationFees)}`}
                  intermediaryFee={`${formatCurrency(transactionSummary.transactionSummary.annualFinancialIntermediaryFees)}`}
                  intermediaryFeeVat={`${formatCurrency(transactionSummary.transactionSummary.vatOnAnnualFinancialIntermediaryFees)}`}
                />
                <InvestmentPriceMovement
                  data-testid='transactionSummaryInvestmentPriceMovementTable'
                  priceMovement={`${formatCurrency(transactionSummary.transactionSummary.investmentPriceMovement)}`}
                  totalReturn={`${formatCurrency(transactionSummary.transactionSummary.totalInvestmentReturn)}`}
                />
              </>
            ) : (
              <>
                <TransactionSummaryHeadingRow
                  testid='transactionSummaryInvestmentPriceMovement'
                  data-testid='transactionSummaryInvestmentPriceMovementRow'
                  heading='Investment price movement'
                  amount={`${formatCurrency(transactionSummary.transactionSummary.investmentPriceMovement)}`}
                />
                <TransactionSummaryHeadingRow
                  testid='transactionSummaryTotalInvestmentReturn'
                  data-testid='transactionSummaryTotatlInvestmentReturnRow'
                  heading='Total investment return'
                  amount={`${formatCurrency(transactionSummary.transactionSummary.totalInvestmentReturn)}`}
                />
              </>
            )}
            <TransactionSummaryExplanation />
            <Show when={includeIRR}>
              <InternalRateOfReturnTable
                data-testid='transactionSummaryInternalRateOfReturnTable'
                fromDate={`${formatDate(transactionSummary.transactionSummary.openingBalanceDate)}`}
                toDate={`${formatDate(transactionSummary.transactionSummary.closingBalanceDate)}`}
                gross={{
                  internalRateOfReturn: 'Gross IRR',
                  tooltipContent:
                    'The gross IRR reflects the return an investor earns on their investment over a specific period before administration and intermediary fees are deducted.',
                  specifiedPeriod: `${transactionSummary.transactionSummary.grossIRRForPeriod}`,
                  oneYear: `${transactionSummary.transactionSummary.grossIRROneYear}`,
                  threeYears: `${transactionSummary.transactionSummary.grossIRRThreeYears}`,
                  fiveYears: `${transactionSummary.transactionSummary.grossIRRFiveYears}`,
                  testid: 'transactionSummaryGross'
                }}
                net={{
                  internalRateOfReturn: 'Net IRR',
                  tooltipContent:
                    'The net IRR reflects the return an investor earns on their investment over a specific period, after all fees have been deducted.',
                  specifiedPeriod: `${transactionSummary.transactionSummary.netIRRForPeriod}`,
                  oneYear: `${transactionSummary.transactionSummary.netIRROneYear}`,
                  threeYears: `${transactionSummary.transactionSummary.netIRRThreeYears}`,
                  fiveYears: `${transactionSummary.transactionSummary.netIRRFiveYears}`,
                  testid: 'transactionSummaryNet'
                }}
              />
            </Show>
          </div>
        )}
      </StatementDropdown>
    </>
  );
};

export default TransactionSummaryDropdown;
